<template>
  <div class="pt-8 flex flex-col lg:flex-row p-4 min-h-screen bg-light-bg dark:bg-dark-bg dark:text-dark-text">
    <AppSidebar />

    <div class="flex flex-col flex-grow p-6 lg:p-12 space-y-4 h-full">
      <div v-if="message" :class="messageType" 
          class="fixed top-0 left-0 right-0 z-5 p-4 rounded-lg mb-4 text-center 
                  bg-white dark:bg-dark-box-bg dark:text-dark-text dark:border 
                  dark:border-dark-border">
          {{ message }}
          <div class="absolute bottom-0 left-0 h-1 rounded-lg" 
              :style="{ width: progress + '%', backgroundColor: getProgressColor(), borderRadius: '8px' }">
          </div>
      </div>

      <h1 class="text-xl md:text-2xl font-bold mb-4 text-center lg:text-left">Archiwum Zleceń</h1>

      <div class="bg-white p-4 rounded-lg shadow-md w-full h-auto overflow-visible dark:bg-dark-box-bg dark:border dark:border-dark-border dark:text-dark-text">
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-4 sm:space-y-0">
          <h2 class="text-lg font-semibold text-gray-700 dark:text-dark-text">Lista Archiwalnych Zleceń:</h2>

          <!-- Wyszukiwanie -->
          <DynamicSearch
              :data="orders"
              :keysToSearch="['mpk', 'company.companyName', 'passengerName', 'driverName', 'orderStatus']"
              @search-results="handleSearchResults"
              class="mt-4 bg-gray-200 dark:bg-dark-box-bg text-gray-900 dark:text-dark-text w-full md:w-auto"
              placeholder="Wyszukaj..."
          />
        </div>

        <div class="overflow-x-auto w-full">
          <table class="min-w-full bg-white border border-gray-300 rounded-lg dark:bg-dark-table-bg dark:border-dark-border">
            <thead>
            <tr class="bg-gray-50 dark:bg-dark-table-header dark:text-dark-text">
              <th class="py-3 px-4 border-b border-gray-300 text-left text-sm font-semibold text-gray-700 dark:border-dark-border dark:text-dark-text">ID Zlecenia</th>
              <th class="py-3 px-4 border-b border-gray-300 text-left text-sm font-semibold text-gray-700 dark:border-dark-border dark:text-dark-text">Data Archiwum</th>
              <th class="py-3 px-4 border-b border-gray-300 text-left text-sm font-semibold text-gray-700 dark:border-dark-border dark:text-dark-text"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="filteredOrders.length === 0">
              <td colspan="6" class="text-center py-4 text-red-500 font-semibold dark:text-dark-status-unavailable">Brak archiwalnych zleceń</td>
            </tr>
            <tr 
              v-else 
              v-for="(order) in paginatedOrders" 
              :key="order.id" 
              class="border-b border-gray-300 dark:border-dark-border hover:bg-light-hover dark:hover:bg-dark-hover hover:cursor-pointer"
            >
              <td class="py-3 px-4 dark:text-dark-text">{{ order.mpk }}</td>
              <td class="py-3 px-4 dark:text-dark-text">{{ formatDate(order.archivedAt) }}</td>
              <td class="py-3 px-4 text-right dark:text-dark-text">
                <div class="relative inline-block text-left">
                  <button @click="toggleDropdown(order.id)" class="text-gray-500 hover:text-gray-700 dark:text-dark-text dark:hover:text-gray-400">
                    <EllipsisVerticalIcon class="h-5 w-5" />
                  </button>
                  <div v-if="dropdownOpen === order.id" class="origin-top-right absolute left-0 ml-[-10rem] mt-[-2.2rem] w-40 focus:outline-none">
                    <div class="py-1.5 absolute flex space-x-2 bg-gray-200 dark:bg-gray-900 rounded">
                      <button @click="goToProfile(order)" class="text-left px-2 py-2 text-sm text-teal-700 dark:text-teal-400 flex items-center space-x-2">
                        <DocumentIcon class="w-4 h-4" />
                      </button>
                      <button @click="restoreOrder(order.id)" class="text-left px-2 py-2 text-sm text-teal-700 dark:text-teal-400 flex items-center space-x-2">
                        <PlusIcon class="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-4 flex items-center justify-between">
          <div class="text-sm text-light-text dark:text-gray-400">
            Wyświetlono {{ (currentPage - 1) * itemsPerPage + paginatedOrders.length }} z {{ filteredOrders.length }} archiwalnych zleceń
          </div>

          <!-- Paginacja -->
          <div class="flex flex-wrap justify-center sm:justify-end items-center gap-2">
            <!-- Pierwsza strona -->
            <button v-if="currentPage > 1" @click="goToPage(1)" class="px-3 py-1 bg-light-table-header text-sm font-medium rounded-md dark:bg-dark-table-header dark:text-dark-text">
              Pierwsza strona
            </button>
            <!-- Poprzednia strona -->
            <button v-if="currentPage > 1" @click="prevPage" class="px-3 py-1 bg-light-table-header text-sm font-medium rounded-md dark:bg-dark-table-header dark:text-dark-text">
              &laquo; Poprzednia
            </button>
            <!-- Widoczne strony -->
            <button v-for="page in visiblePages" :key="page" @click="goToPage(page)"
                    :class="[currentPage === page ? 'bg-teal-700 text-white' : 'bg-gray-200 dark:bg-dark-box-bg text-gray-700 dark:text-dark-text']"
                    class="px-3 py-1 rounded-lg text-sm cursor-pointer">
              {{ page }}
            </button>
            <!-- Następna strona -->
            <button v-if="currentPage < totalPages" @click="nextPage" class="px-3 py-1 bg-light-table-header text-sm font-medium rounded-md dark:bg-dark-table-header dark:text-dark-text">
              Następna &raquo;
            </button>
            <!-- Ostatnia strona -->
            <button v-if="currentPage < totalPages" @click="goToPage(totalPages)" class="px-3 py-1 bg-light-table-header text-sm font-medium rounded-md dark:bg-dark-table-header dark:text-dark-text">
              Ostatnia strona
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>



<script>
  import { useAuthStore } from '@/stores/authStore';
  import orderService from '@/services/orderService';
  import AppSidebar from '@/components/AppSidebar.vue';
  import DynamicSearch from '../search/DynamicSearch.vue';
  import { EllipsisVerticalIcon, DocumentIcon, PlusIcon } from "@heroicons/vue/24/solid";

  export default {
    name: "OrdersPageDispatcher",
    components: {
      EllipsisVerticalIcon,
      DocumentIcon,
      PlusIcon,
      AppSidebar,
      DynamicSearch
    },
    computed: {
      paginatedOrders() {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredOrders.slice(start, end);
      }
    },
    setup() {
      const authStore = useAuthStore();
      if (authStore.isAuthenticated) {
        authStore.checkExpiration();
      }
      return { user: authStore.user };
    },
    data() {
        return {
            filteredOrders: [],
            orders: [],
            dropdownOpen: null,
            message: '',
            messageType: '',
            progress: 0,
            loading: false,

            currentPage: 1,
            itemsPerPage: 15,
            totalPages: 0,
            visiblePages: []
          };
    },
    mounted() {
      this.calculateItemsPerPage();
      window.addEventListener("resize", this.calculateItemsPerPage);

      const pageFromUrl = parseInt(this.$route.query.currentPage) || 1;
      this.currentPage = pageFromUrl;

      this.fetchArchiveOrders().then(() => {
        this.calculateTotalPages(); // Oblicz paginację
      });
    },
    beforeUnmount() {
      window.removeEventListener("resize", this.calculateItemsPerPage);
    },
    methods: {
      calculateTotalPages() {
        this.totalPages = Math.ceil(this.filteredOrders.length / this.itemsPerPage) || 1;
        this.updateVisiblePages();
      },
      updateVisiblePages() {
        const range = 5; // Liczba stron widocznych na raz
        const start = Math.max(1, this.currentPage - Math.floor(range / 2));
        const end = Math.min(start + range - 1, this.totalPages);

        this.visiblePages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
      },
      goToPage(page) {
        this.currentPage = page;
        this.updateVisiblePages();
      },
      nextPage() {
        if (this.currentPage < this.totalPages) {
          this.currentPage++;
          this.updateVisiblePages();
        }
      },
      prevPage() {
        if (this.currentPage > 1) {
          this.currentPage--;
          this.updateVisiblePages();
        }
      },
      calculateItemsPerPage() {
        const width = window.innerWidth;

        if (width >= 3840) {
          this.itemsPerPage = 24; // 4K monitor
        } else if (width >= 2560) {
          this.itemsPerPage = 15; // 2K monitor
        } else if (width >= 1920) {
          this.itemsPerPage = 8; // Full HD monitor
        } else {
          this.itemsPerPage = 5; // Standardowa wartość
        }

        this.calculateTotalPages();
      },
      showMessage(msg, type) {
        this.message = msg;
        this.messageType = type === 'success' ? 'success-message' : 'error-message';
        this.progress = 0;

        const interval = 30;
        const duration = 5000;
        const step = 100 / (duration / interval);

        const progressInterval = setInterval(() => {
          this.progress += step;
          if (this.progress >= 100) {
            clearInterval(progressInterval);
            this.progress = 0;
            this.message = '';
            this.messageType = '';
          }
        }, interval);
      },
      getProgressColor() {
        return this.messageType === 'success-message' ? '#16a34a' : '#b91c1c';
      },
      async fetchArchiveOrders() {
        try {
          const response = await orderService.getArchiveOrdersByDispatcher(this.user.uid);
          this.orders = response.data.map(order => ({
            ...order,

          }));
          this.filteredOrders = this.orders;
        } catch (error) {
          console.error('Błąd podczas pobierania zamówień:', error);
        }
      },
      async restoreOrder(orderId) {
        if (confirm("Czy na pewno chcesz przywrócić to zamówienie?")) {
          try {
            await orderService.restoreOrder(orderId);
            await this.fetchArchiveOrders();
            this.showMessage("Zlecenie zostało pomyślnie przywrócone.", "success");
          } catch (error) {
            console.error('Błąd podczas usuwania zamówienia:', error);
            this.showMessage("Wystąpił błąd podczas usuwania zlecenia.", "error");
          }
        }
      },
      goToProfile(order) {
        if (this.user.role === 'ROLE_ADMIN') {
          this.$router.push({
            name: 'OrderProfileAdmin', 
            params: {id: order.id},
            query: { 
              returnTo: this.$route.name,
            }
          });
        } else {
          this.$router.push({
            name: 'OrderProfileDispatcher', 
            params: {id: order.id},
            query: { 
              returnTo: this.$route.name,
            }
          });
        }
      },
      formatDate(dateString) {
        const date = new Date(dateString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}`;
      },
      toggleDropdown(orderId) {
        this.dropdownOpen = this.dropdownOpen === orderId ? null : orderId;
      },
      handleSearchResults(filteredData) {
        this.filteredOrders = filteredData;
        this.calculateTotalPages();

        this.goToPage(1);
      },
    }
  };
  </script>
  